import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError, imgSrc } from "../../lib/api";
import { Switch } from "../../components/ui/switch";
import { fmtDate } from "../../components/site/Promotions";
import { EMPTY_PROMO, PromoForm } from "./PromoForm";

const stateOf = (p, today) => {
  if (!p.active) return ["Off", "bg-gray-200 text-gray-600"];
  if (p.starts_on && p.starts_on > today) return [`Starts ${fmtDate(p.starts_on)}`, "bg-[#EBD08A] text-black"];
  if (p.ends_on && p.ends_on < today) return ["Ended", "bg-gray-200 text-gray-600"];
  return ["Showing now", "bg-[#FF1F8E] text-white"];
};

const PromoRow = ({ p, today, onEdit, onToggle, onDelete }) => {
  const [label, cls] = stateOf(p, today);
  const img = imgSrc(p);
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-2xl bg-white p-4 ring-1 ring-[#EBD08A]" data-testid={`admin-promo-${p.id}`}>
      {img ? <img src={img} alt="" className="h-16 w-16 rounded-xl object-cover" /> : <div className="h-16 w-16 rounded-xl bg-[#FFF4F8]" />}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">{p.title}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <span className={`rounded-full px-2.5 py-0.5 font-semibold ${cls}`} data-testid={`admin-promo-state-${p.id}`}>{label}</span>
          {(p.starts_on || p.ends_on) && <span className="flex items-center gap-1 text-black/50"><CalendarDays className="h-3 w-3" />{p.starts_on ? fmtDate(p.starts_on) : "Now"} – {p.ends_on ? fmtDate(p.ends_on) : "no end"}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Switch checked={p.active} onCheckedChange={onToggle} aria-label="Show on site" data-testid={`admin-promo-toggle-${p.id}`} />
        <button onClick={onEdit} className="rounded-full p-2 text-black/60 hover:bg-[#FFF4F8]" aria-label="Edit" data-testid={`admin-promo-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
        <button onClick={onDelete} className="rounded-full p-2 text-black/50 hover:bg-red-50 hover:text-red-600" aria-label="Delete" data-testid={`admin-promo-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export default function AdminPromotions() {
  const [items, setItems] = useState([]);
  const [today, setToday] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => { api.get("/admin/promotions").then((r) => { setItems(r.data.items); setToday(r.data.today); }); }, []);

  const saved = (p) => {
    setItems(items.some((x) => x.id === p.id) ? items.map((x) => (x.id === p.id ? p : x)) : [p, ...items]);
    setEditing(null);
  };
  const toggle = async (p, active) => {
    const { id, image_url, storage_path, created_at, ...body } = p;
    try {
      const { data } = await api.put(`/admin/promotions/${id}`, { ...body, active });
      saved(data);
      toast.success(active ? "Promotion is on" : "Promotion turned off");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? Tip: you can just turn it off and reuse it next year.`)) return;
    await api.delete(`/admin/promotions/${p.id}`);
    setItems(items.filter((x) => x.id !== p.id));
    toast.success("Promotion deleted");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Promotions</h1>
          <p className="mt-1 text-sm text-black/55">Holiday specials and deals. Every promotion that's on shows on your homepage and Services page.</p>
        </div>
        {!editing && <button onClick={() => setEditing(EMPTY_PROMO)} className="btn-pink !py-2.5" data-testid="admin-promo-new-btn"><Plus className="h-4 w-4" />New promotion</button>}
      </div>
      {editing && <PromoForm key={editing.id || "new"} initial={editing} onSaved={saved} onCancel={() => setEditing(null)} />}
      <div className="space-y-3" data-testid="admin-promos-list">
        {items.map((p) => <PromoRow key={p.id} p={p} today={today} onEdit={() => setEditing(p)} onToggle={(v) => toggle(p, v)} onDelete={() => remove(p)} />)}
        {items.length === 0 && <p className="rounded-2xl bg-white p-6 text-sm text-black/50" data-testid="admin-promos-empty">No promotions yet. Tap "New promotion" to create your first holiday special.</p>}
      </div>
    </div>
  );
}
